/**
 * Display labels and tier ordering for a coach's marketplace membership plan,
 * shared between the UI badge and server-side renderers (transactional emails).
 * Client-safe — no server-only imports, and deliberately free of any component
 * dependency so a server module can label a plan without pulling in React or
 * icon packages.
 *
 * Aliases: membership plan labels, plan label, PLAN_LABELS, plan display name,
 * plan rank, plan tier order, plan upgrade check, isPlanUpgrade,
 * effective membership plan, GHL scope level, holds the Marketing Suite
 */

import { MembershipPlan } from "~/lib/schemas/db-enums";
import {
  planGrantsMarketingSuite,
  type PlanKey,
} from "~/lib/shared/plan-catalogue";

/** Human-readable name for each membership plan, e.g. `Pro+`. */
export const PLAN_LABELS: Record<MembershipPlan, string> = {
  [MembershipPlan.Standard]: "Standard",
  [MembershipPlan.Pro]: "Pro",
  [MembershipPlan.ProPlus]: "Pro+",
};

/**
 * The plans as a ladder, by what each one includes. Only the relative order
 * carries meaning — these numbers are never stored, displayed, or sent
 * anywhere, so renumbering them is safe as long as the order holds.
 */
const PLAN_RANK: Record<MembershipPlan, number> = {
  [MembershipPlan.Standard]: 0,
  [MembershipPlan.Pro]: 1,
  [MembershipPlan.ProPlus]: 2,
};

/**
 * Whether moving from `previousPlan` to `plan` climbs the ladder.
 *
 * Same-tier moves are not upgrades: the membership page sells each tier both
 * monthly and yearly (`default_pro` / `default_pro_yearly`), and switching
 * interval changes when the coach pays, not what they get. Price is
 * deliberately not consulted — a yearly plan costs more than a monthly one of
 * the SAME tier, and (as the Pro+ monthly → Pro yearly switch shows) can cost
 * more than the tier above it.
 */
export const isPlanUpgrade = (
  plan: MembershipPlan,
  previousPlan: MembershipPlan,
): boolean => PLAN_RANK[plan] > PLAN_RANK[previousPlan];

/** What a coach holds, as far as the membership tier is concerned. */
export type MembershipHoldings = {
  /**
   * The plan key of every subscription the coach currently holds — NOVA and
   * non-NOVA alike. A plan Stripe sells that our catalogue does not name is
   * simply absent; it contributes nothing.
   */
  readonly planKeys: readonly PlanKey[];
  /**
   * A tier already established outside those plan keys: what a legacy product
   * states in its own `plan_type` metadata, or the coach's stored
   * `membership_plan`. Null when there is none. Where several apply, pass the
   * highest.
   */
  readonly statedPlan: MembershipPlan | null;
};

/**
 * The membership tier a set of held things gives. The ONE rule: everything that
 * decides `coaches.membership_plan` goes through here.
 *
 * `MembershipPlan` tiers are retired as products — the Marketing Suite IS Pro+.
 * So any plan containing the Suite gives `ProPlus`, whether it is the
 * standalone Suite or a bundle (NOVA Pro, NOVA Pro+). A legacy product that
 * states its own tier keeps it, which is what leaves a grandfathered `Pro`
 * coach on Pro and stops a cancellation writing them down to Standard. Standard
 * means nothing held gives anything.
 *
 * Pure and total: no Stripe, no database, same answer for the same holdings.
 */
export function membershipPlanForHoldings(
  holdings: MembershipHoldings,
): MembershipPlan {
  const suite = holdings.planKeys.some(planGrantsMarketingSuite)
    ? MembershipPlan.ProPlus
    : MembershipPlan.Standard;
  const stated = holdings.statedPlan ?? MembershipPlan.Standard;
  return isPlanUpgrade(stated, suite) ? stated : suite;
}

/**
 * The Stripe subscription statuses under which a NOVA subscription still gives
 * the coach what it sells.
 *
 * The single definition: `grantsNovaAccess` (`~/lib/server/nova-billing`) is a
 * typed wrapper over {@link novaStatusGrantsAccess}, so the tier derivation
 * here and Nova's own enforcement can never disagree about what a paused,
 * unpaid or abandoned-SCA subscription is worth.
 *
 * Deliberately narrower than "still ours to track": `incomplete`, `unpaid` and
 * `paused` subscriptions exist in Stripe but hand the coach nothing, and
 * reading them as a grant is what would have given an abandoned checkout a full
 * Pro+ scope set.
 */
export const NOVA_ACCESS_GRANTING_STATUSES = [
  "active",
  "trialing",
  "past_due",
] as const;

/** Whether a NOVA subscription in this Stripe status currently grants access. */
export function novaStatusGrantsAccess(status: string): boolean {
  return (NOVA_ACCESS_GRANTING_STATUSES as readonly string[]).includes(status);
}

/**
 * The Stripe subscription statuses under which a NOVA bundle also hands the
 * coach the Marketing Suite it contains.
 *
 * Narrower than {@link NOVA_ACCESS_GRANTING_STATUSES} by exactly one status,
 * and deliberately its own constant rather than a filter of it: a free trial
 * covers NOVA and nothing else. `trialing` still grants NOVA — the two sets
 * answer different questions, and a trialist keeps every bit of Nova — but it
 * buys no Marketing Suite, so it lifts no GoHighLevel scope level and
 * provisions no workspace. `past_due` is dunning rather than a trial — but only
 * for a coach who has actually paid, which the status alone does not say; see
 * {@link novaBundleGrantsSuite}.
 */
export const SUITE_GRANTING_STATUSES = ["active", "past_due"] as const;

/** Whether a NOVA subscription in this Stripe status currently gives the Suite. */
export function novaStatusGrantsSuite(status: string): boolean {
  return (SUITE_GRANTING_STATUSES as readonly string[]).includes(status);
}

/** The coach columns the effective tier is derived from. */
export type EffectivePlanCoach = {
  /** `coaches.membership_plan` — what the coach holds OUTSIDE Nova. */
  readonly membershipPlan: MembershipPlan;
  /**
   * `coaches.nova_plan_key`, `coaches.nova_subscription_status` and
   * `coaches.nova_paid_started_at`. Optional so a caller holding a listing row
   * rather than a full coach record still compiles; absent reads as "no NOVA
   * subscription" (and, for the marker, as "never paid"), which can only fail
   * to RAISE the tier, never lower it.
   */
  readonly novaPlanKey?: PlanKey | null;
  readonly novaSubscriptionStatus?: string | null;
  readonly novaPaidStartedAt?: Date | null;
};

/**
 * Whether the coach's NOVA subscription is handing them the Marketing Suite
 * right now.
 *
 * The status carries it for `active`, but not on its own for `past_due`. A
 * bundle whose FIRST invoice fails goes `trialing` → `past_due` having never
 * been paid for, and reading that as dunning gave a coach who paid us nothing
 * a Pro+ scope level and a permanent GoHighLevel workspace. `novaPaidStartedAt`
 * is stamped only from an observed `active`, so it is exactly the record of
 * "money has changed hands at least once" that separates dunning from a trial
 * that lapsed into failure.
 */
function novaBundleGrantsSuite(coach: EffectivePlanCoach): boolean {
  const status = coach.novaSubscriptionStatus;
  return (
    coach.novaPlanKey != null &&
    planGrantsMarketingSuite(coach.novaPlanKey) &&
    status != null &&
    novaStatusGrantsSuite(status) &&
    (status === "active" || coach.novaPaidStartedAt != null)
  );
}

/**
 * The tier a coach is actually entitled to right now: the higher of what they
 * hold outside Nova and what their NOVA subscription carries.
 *
 * `coaches.membership_plan` deliberately records only the first of those —
 * legacy `plan_type` subscriptions, lifetime purchases, the standalone
 * Marketing Suite, hand-set values. NOVA events never write it. A bundle
 * (NOVA Pro, NOVA Pro+) contains the Marketing Suite, and the Suite IS Pro+, so
 * holding one RAISES the level for as long as it is PAID for
 * ({@link novaBundleGrantsSuite}) — and the moment it stops (cancelled,
 * switched down to Core, `incomplete`, `paused`, `unpaid`, or never yet paid
 * for at all) the level simply falls back to the stored column.
 *
 * Deriving rather than writing is what makes that fallback safe. A written
 * version had to guess what the coach still held when the bundle ended, and
 * guessed from live Stripe subscriptions — which cannot see a lifetime purchase
 * or a hand-set tier, so a Pro coach who tried the bundle was written down to
 * Standard and lost their GoHighLevel scopes. Nothing here can lower anything.
 *
 * Every place that turns a coach into GoHighLevel scopes, a provisioning
 * decision or a paid feature gate reads this, never the raw column.
 */
export function effectiveMembershipPlan(
  coach: EffectivePlanCoach,
): MembershipPlan {
  const novaSuite = novaBundleGrantsSuite(coach);

  return membershipPlanForHoldings({
    planKeys: novaSuite && coach.novaPlanKey ? [coach.novaPlanKey] : [],
    statedPlan: coach.membershipPlan,
  });
}

/**
 * Whether the coach holds the Marketing Suite right now: a paid NOVA Pro or
 * Pro+, the Suite on its own, or a legacy or lifetime Pro or Pro+ tier. Each of
 * these gives her a full GoHighLevel workspace to work in.
 *
 * Read from the tier, not from a provisioned workspace. A workspace outlives
 * the plan: coaches who signed up through GoHighLevel before the Suite existed
 * keep one at the Standard level, and so does a coach whose NOVA Pro lapsed.
 * Neither has the Suite, so neither should be sent to it as if she did.
 */
export function holdsMarketingSuite(coach: EffectivePlanCoach): boolean {
  return effectiveMembershipPlan(coach) !== MembershipPlan.Standard;
}
